import { useState } from 'react';

import { CheckCircle2, Home, Mail, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';

import { Button } from '@src/components/ui/button';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

const CONFETTI_COLORS = ['#1e638d', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe'];

function generateConfettiPieces() {
  return Array.from({ length: 50 }, () => ({
    rotate: Math.random() * 360,
    duration: 2 + Math.random() * 2,
    delay: Math.random() * 0.5,
    left: Math.random() * 100,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
  }));
}

export function SuccessComponent({confetti = false, title, subtitle, nextSteps}) {

  const onNavigateToTop = useNavigateToTop();
  // showConfetti derives directly from prop — no state/effect needed.
  const showConfetti = Boolean(confetti);
  // useState lazy initializer is exempt from react-hooks/purity (unlike useMemo, whose callback runs during render).
  // We never call the setter — the array is stable for the component lifetime.
  const [confettiPieces] = useState(generateConfettiPieces);

  return (
    <div>
      {/* Confetti Effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {confettiPieces.map((piece, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              initial={{ y: 0, opacity: 1, rotate: 0 }}
              animate={{
                y: window.innerHeight + 100,
                opacity: 0,
                rotate: piece.rotate,
              }}
              style={{
                left: `${piece.left}%`,
                top: '-5%',
                backgroundColor: piece.color,
              }}
              transition={{
                duration: piece.duration,
                delay: piece.delay,
                ease: 'easeOut',
              }}
            />
          ))}
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Card */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            initial={{ opacity: 0, y: 20 }}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-8 md:p-12"
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="flex justify-center mb-8"
              transition={{
                type: 'spring',
                stiffness: 200,
                damping: 15,
                delay: 0.2,
              }}
            >
              <div className="relative">
                <motion.div
                  className="absolute inset-0 bg-linear-to-br from-[#1e638d] to-blue-600 rounded-full blur-xl opacity-50"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 0.3, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />

                <CheckCircle2 strokeWidth={2} className="w-20 h-20 md:w-24 md:h-24 text-[#1e638d] relative z-10" />
              </div>
            </motion.div>

            {/* Title */}
            <motion.h1
              transition={{ delay: 0.4 }}
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 10 }}
              className="text-center text-gray-900 dark:text-white mb-4"
            >
              {title}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              transition={{ delay: 0.5 }}
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 10 }}
              className="text-center text-gray-600 dark:text-gray-300 mb-8 max-w-xl mx-auto"
            >
              {subtitle}
            </motion.p>

            {/* Action Buttons */}
            <motion.div
              transition={{ delay: 0.6 }}
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 10 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
            >
              <Button
                className="bg-linear-to-r from-[#1e638d] to-blue-600 hover:from-[#1a5578] hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 group"
                onClick={() => onNavigateToTop('/')}
              >
                <Home className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                Return to Homepage
              </Button>

              <Button
                variant="outline"
                className="border-[#1e638d] text-[#1e638d] hover:bg-[#1e638d]/10 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-400/10 transition-all duration-300 group"
                onClick={() => onNavigateToTop('/', 'platform-overview')}
              >
                <Sparkles className="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform" />
                Learn More About BOSSS
              </Button>
            </motion.div>

            {/* Divider */}
            <div className="w-full h-px bg-linear-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent mb-8" />

            {/* Next Steps Card */}
            <motion.div
              transition={{ delay: 0.7 }}
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 10 }}
              className="bg-linear-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 rounded-xl p-6 border border-blue-200/50 dark:border-blue-800/50"
            >
              <h3 className="flex items-center gap-2 text-gray-900 dark:text-white mb-4">
                <div className="w-8 h-8 bg-[#1e638d] rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                Next Steps
              </h3>

              <ul className="space-y-3">
                {nextSteps.map((step, index) => (
                  <motion.li
                    key={index}
                    animate={{ opacity: 1, x: 0 }}
                    initial={{ opacity: 0, x: -10 }}
                    transition={{ delay: 0.8 + (index * 0.1) }}
                    className="flex items-start gap-3 text-gray-700 dark:text-gray-300"
                  >
                    <div className="w-5 h-5 rounded-full bg-[#1e638d]/20 dark:bg-blue-400/20 flex items-center justify-center shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-[#1e638d] dark:bg-blue-400" />
                    </div>

                    <span>{step}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="text-center mt-8 text-gray-600 dark:text-gray-400"
            >
              <p>
                Questions? Reach us at
                {' '}

                <a
                  href="mailto:info@secureonelabs.com"
                  className="text-[#1e638d] hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                >
                  info@secureonelabs.com
                </a>
              </p>
            </motion.div>

            {/* Social Media Links */}
            {/* <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.1 }}
                    className="flex justify-center gap-4 mt-6"
                    >
                    <p className="text-gray-600 dark:text-gray-400 mr-2">Follow Us:</p>
                    <a
                        href="https://linkedin.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-[#1e638d] dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                    >
                        <Linkedin className="w-5 h-5" />
                    </a>
                    <a
                        href="https://twitter.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-[#1e638d] dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                    >
                        <Twitter className="w-5 h-5" />
                    </a>
                    </motion.div> */}
          </motion.div>

        </div>
      </div>
    </div>
  );
}
